import { useEffect, useState, useRef, useCallback } from 'react';
import { TrashIcon, ChevronLeftIcon, EllipsisVerticalIcon, PencilIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/solid';
import { XMarkIcon as XMarkIconOutline, CheckIcon as CheckIconOutline } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { createNote, updateNote, getClusters } from '../../moduleServices/notepad_services';
import SpinnerLoad from '../../moduleComponents/spinner_load';
import ConfirmModal from '../../moduleComponents/ConfirmModal';
import { useClickOutside } from 'services/useClickOutside';
import useOverlayHistory from 'services/useOverlayHistory';
import useScrollLock from 'services/useScrollLock';
import DropdownCluster from '../../moduleComponents/DropdownCluster';
import { getLightBackgroundColor } from '../../moduleServices/colorUtils';

// ##### REFACTORED: Simplified props - removed onSuccess (no longer needed)
const DrawerNoteForm = ({ isOpen, onClose, onOptimisticUpdate, editNote, onDelete, clusters: clustersProp }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    cluster_id: null
  });
  // ##### FIX: Use clusters from prop (context) instead of fetching
  const [clusters, setClusters] = useState(clustersProp || []);
  const [isLoadingClusters, setIsLoadingClusters] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showClusterDropdown, setShowClusterDropdown] = useState(false);
  const clusterDropdownRef = useRef(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [previousNoteData, setPreviousNoteData] = useState(null);
  const timeoutRef = useRef(null);
  const menuRef = useRef(null);
  const formDataRef = useRef(formData);
  const formRef = useRef(null);
  const handleSubmitRef = useRef(() => {});
  const isEditModeRef = useRef(isEditMode);
  isEditModeRef.current = isEditMode;

  const interceptPop = useCallback(() => {
    if (!isEditModeRef.current) return false;
    const fakeEvent = { preventDefault: () => {} };
    Promise.resolve(handleSubmitRef.current(fakeEvent)).then(() => setIsEditMode(false)).catch(() => {});
    return true;
  }, []);

  const handleRequestClose = useOverlayHistory(isOpen, onClose, {
    stateKey: 'notepadDrawer',
    interceptPop,
  });

  // Menus only — do not useClickOutside to close the drawer (backdrop owns that; ADR #006)
  useClickOutside([menuRef], () => setShowMenu(false), showMenu);
  useClickOutside([clusterDropdownRef], () => setShowClusterDropdown(false), showClusterDropdown);

  // Lock body scroll when drawer is open
  useScrollLock(isOpen);

  // Animation logic
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        timeoutRef.current = null;
      }, 300);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen]);

  // ##### FIX: Use clusters from prop, fallback to fetch if not provided
  useEffect(() => {
    if (isOpen) {
      if (clustersProp && clustersProp.length > 0) {
        // Use clusters from context (already loaded)
        setClusters(clustersProp);
        setIsLoadingClusters(false);
      } else {
        // Fallback: fetch clusters if not provided
        const loadClusters = async () => {
          setIsLoadingClusters(true);
          try {
            const data = await getClusters();
            setClusters(data);
          } catch (error) {
            console.error('Error loading clusters:', error);
            // Don't break drawer if clusters fail to load
            setClusters([]);
          } finally {
            setIsLoadingClusters(false);
          }
        };
        loadClusters();
      }
    }
  }, [isOpen, clustersProp]);

  // ##### REFACTORED: Reset form data only when drawer opens or note changes
  // No synchronization needed - when viewing, use editNote directly; when editing, use formData
  useEffect(() => {
    if (isOpen) {
      if (editNote) {
        // Existing note: initialize formData but start in view mode
        setFormData({
          title: editNote.title || '',
          content: editNote.content || '',
          cluster_id: editNote.cluster?.id || null
        });
        setIsEditMode(false); // Start in view mode for existing notes
      } else {
        // New note: empty formData and start in edit mode
        setFormData({
          title: '',
          content: '',
          cluster_id: null
        });
        setIsEditMode(true); // Start in edit mode for new notes
      }
      setShowDeleteModal(false);
      setShowMenu(false);
      setShowClusterDropdown(false);
    }
  }, [editNote?.id, isOpen]); // Only reset when note ID changes or drawer opens/closes

  // ##### Check if form data has changed
  const hasChanges = useCallback((formDataToCheck) => {
    if (!editNote) {
      // For new notes, allow save with empty title (content can be the only change)
      return true;
    }
    
    // Compare with original note data
    const originalTitle = (editNote.title || '').trim();
    const originalContent = (editNote.content || '').trim();
    const originalClusterId = editNote.cluster?.id || null;
    
    const currentTitle = (formDataToCheck.title || '').trim();
    const currentContent = (formDataToCheck.content || '').trim();
    const currentClusterId = formDataToCheck.cluster_id || null;
    
    return (
      originalTitle !== currentTitle ||
      originalContent !== currentContent ||
      originalClusterId !== currentClusterId
    );
  }, [editNote]);

  // Handle ESC key - save in edit mode, close drawer in view mode
  useEffect(() => {
    const handleKeyDown = async (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        e.preventDefault();
        
        if (isEditMode) {
          // In edit mode: save the note (if there are changes) or just exit edit mode
          const currentFormData = formDataRef.current;
          if (editNote && !hasChanges(currentFormData)) {
            setIsEditMode(false);
          } else {
            const fakeEvent = { preventDefault: () => {} };
            await handleSubmit(fakeEvent);
          }
        } else {
          // In view mode: close the drawer via history trap
          handleRequestClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditMode, isOpen, isSubmitting, editNote, hasChanges, handleRequestClose]);

  // Keep formDataRef in sync with formData
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isEmptyNote = (data) => !(data.title || '').trim() && !(data.content || '').trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Always use the most current formData from ref
    const currentFormData = formDataRef.current;
    
    // Check if there are any changes before submitting
    if (editNote && !hasChanges(currentFormData)) {
      // No changes detected, just exit edit mode
      setIsEditMode(false);
      return;
    }

    // Create: if note would be empty, discard (close without creating)
    if (!editNote && isEmptyNote(currentFormData)) {
      handleRequestClose();
      return;
    }

    // Edit: if note would become empty, don't save (discard changes)
    if (editNote && isEmptyNote(currentFormData)) {
      toast.error('Note must have a title and/or content.');
      return;
    }

    setIsSubmitting(true);
    
    // ##### REFACTORED: Simplified save flow
    // Single optimistic update - notes array is updated, editNote prop updates automatically
    try {
      if (editNote) {
        // Save previous state for rollback
        setPreviousNoteData({
          title: editNote.title,
          content: editNote.content,
          updated_at: editNote.updated_at
        });
        
        // Get cluster object for optimistic update
        const selectedCluster = currentFormData.cluster_id 
          ? clusters.find(c => c.id === currentFormData.cluster_id)
          : null;
        
        // Optimistic update BEFORE API call (updates notes array immediately)
        const optimisticData = {
          id: editNote.id,
          title: currentFormData.title,
          content: currentFormData.content,
          updated_at: new Date().toISOString(),
          cluster: selectedCluster || null,
          cluster_id: currentFormData.cluster_id
        };
        
        // Update notes array immediately (editNote prop will update automatically)
        if (onOptimisticUpdate) {
          onOptimisticUpdate(optimisticData);
        }
        
        // Switch to view mode immediately (will use editNote prop which is now updated)
        setIsEditMode(false);
        
        // API call (updates server/IndexedDB)
        const updatedNote = await updateNote(editNote.id, currentFormData);
        toast.success('Note updated successfully!');
        
        // Update with server response (in case server modified something)
        if (onOptimisticUpdate) {
          onOptimisticUpdate({
            id: editNote.id,
            title: updatedNote.title,
            content: updatedNote.content,
            updated_at: updatedNote.updated_at,
            cluster: updatedNote.cluster || null,
            cluster_id: updatedNote.cluster?.id || null
          });
        }
        
        // Keep drawer open - editNote prop is already updated via optimistic update
      } else {
        // Create new note
        const newNote = await createNote(currentFormData);
        toast.success('Note created successfully!');
        
        // Get cluster object for optimistic update
        const selectedCluster = newNote.cluster || 
          (currentFormData.cluster_id ? clusters.find(c => c.id === currentFormData.cluster_id) : null);
        
        // Add to notes array via optimistic update
        if (onOptimisticUpdate) {
          onOptimisticUpdate({
            id: newNote.id,
            title: newNote.title || currentFormData.title,
            content: newNote.content || currentFormData.content,
            cluster: selectedCluster || null,
            cluster_id: newNote.cluster_id || currentFormData.cluster_id,
            created_at: newNote.created_at || new Date().toISOString(),
            updated_at: newNote.updated_at || new Date().toISOString()
          });
        }
        
        // Close drawer after creating new note (sync history then close)
        handleRequestClose();
      }
      setPreviousNoteData(null); // Clear rollback data on success
    } catch (error) {
      // Rollback optimistic update on error
      if (editNote && previousNoteData) {
        setFormData({
          title: previousNoteData.title,
          content: previousNoteData.content,
          cluster_id: editNote.cluster?.id || null
        });
        setIsEditMode(true); // Return to edit mode
        if (onOptimisticUpdate) {
          onOptimisticUpdate({
            id: editNote.id,
            title: previousNoteData.title,
            content: previousNoteData.content,
            updated_at: previousNoteData.updated_at,
            cluster: editNote.cluster || null,
            cluster_id: editNote.cluster?.id || null
          });
        }
      }
      toast.error(error.response?.data?.error || 'Failed to save note');
    } finally {
      setIsSubmitting(false);
    }
  };

  handleSubmitRef.current = handleSubmit;

  // ##### Handle edit mode toggle
  // ##### REFACTORED: Initialize formData when entering edit mode
  const handleEditModeToggle = () => {
    if (editNote) {
      // Initialize formData with current editNote values when entering edit mode
      setFormData({
        title: editNote.title || '',
        content: editNote.content || '',
        cluster_id: editNote.cluster?.id || null
      });
    }
    setIsEditMode(true);
  };

  // ##### Handle cancel edit
  // ##### REFACTORED: No need to reset formData - when not editing, we use editNote directly
  const handleCancelEdit = () => {
    if (editNote) {
      setIsEditMode(false);
      // FormData will be ignored when not in edit mode (we use editNote directly)
    } else {
      handleRequestClose();
    }
  };

  // ##### Handle menu toggle
  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  // ##### Handle delete click - opens confirmation modal
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowDeleteModal(true);
  };

  // ##### Handle confirm delete - executes delete action
  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    setIsSubmitting(true);
    try {
      await onDelete(editNote.id);
      toast.success('Note deleted successfully!');
      handleRequestClose();
    } catch (error) {
      toast.error('Failed to delete note');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ##### Handle cancel delete - closes modal
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  // ##### Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!shouldRender) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-50 ${
          isVisible ? 'opacity-30' : 'opacity-0'
        }`}
        onClick={handleRequestClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[60%] max-w-[800px] bg-white shadow-xl z-[60] transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col relative">
          {/* ##### Delete Confirmation Card */}
          {editNote && (
            <ConfirmModal
              isOpen={showDeleteModal}
              onClose={handleCancelDelete}
              onConfirm={handleConfirmDelete}
              title="Delete Note"
              message={`Are you sure you want to delete "${(editNote?.title || '').trim() || 'this note'}"? This action cannot be undone.`}
              confirmText="Delete"
              cancelText="Cancel"
              confirmButtonClass="bg-red-600 hover:bg-red-700 text-white shadow"
            />
          )}

          {/* Header */}
          <div 
            className="flex items-center justify-between px-4 py-2 bg-white border-b"
            style={(() => {
              // Get cluster color from formData (edit mode) or editNote (view mode)
              let clusterColor = null;
              if (isEditMode) {
                if (formData.cluster_id) {
                  const selectedCluster = clusters.find(c => c.id === formData.cluster_id);
                  if (selectedCluster) {
                    clusterColor = selectedCluster.color;
                  }
                }
                // If cluster_id is null, no border color
              } else if (editNote?.cluster?.color) {
                clusterColor = editNote.cluster.color;
              }
              return clusterColor ? { borderBottomColor: clusterColor, borderBottomWidth: '3px' } : {};
            })()}
          >
            <button
              onClick={handleRequestClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800 flex-1 text-center">
              {editNote ? (isEditMode ? 'Edit Note' : 'View Note') : 'New Note'}
            </h2>
            <div className="flex items-center gap-2">
              {isEditMode ? (
                <>
                  {/* Cancel button */}
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                    className="p-2 rounded-lg transition-colors text-gray-600 hover:bg-gray-100"
                  >
                    <XMarkIconOutline className="h-5 w-5" strokeWidth={2.5} />
                  </button>
                  {/* Save button */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleSubmit(e);
                    }}
                    disabled={isSubmitting || (editNote ? !hasChanges(formData) : !formData.title.trim() && !formData.content.trim())}
                    className="p-2 rounded-lg transition-colors text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting ? (
                      <SpinnerLoad size="small" />
                    ) : (
                      <CheckIconOutline className="h-5 w-5" strokeWidth={2.5} />
                    )}
                  </button>
                  {/* Three Dots menu - always visible in edit mode for existing notes */}
                  {editNote && (
                    <div className="relative" ref={menuRef}>
                      <button
                        onClick={handleMenuToggle}
                        disabled={isSubmitting}
                        className="p-2 rounded-lg transition-colors text-gray-600 hover:bg-gray-100"
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                      </button>
                      
                      {/* Dropdown Menu */}
                      {showMenu && (
                        <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px] z-50">
                          <button
                            onClick={handleDeleteClick}
                            disabled={isSubmitting}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {editNote && (
                    <>
                      <button
                        onClick={handleEditModeToggle}
                        className="p-2 rounded-lg transition-colors text-gray-600 hover:bg-gray-100"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <div className="relative" ref={menuRef}>
                        <button
                          onClick={handleMenuToggle}
                          disabled={isSubmitting}
                          className="p-2 rounded-lg transition-colors text-gray-600 hover:bg-gray-100"
                        >
                          <EllipsisVerticalIcon className="h-5 w-5" />
                        </button>
                        
                        {/* Dropdown Menu */}
                        {showMenu && (
                          <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px] z-50">
                            <button
                              onClick={handleDeleteClick}
                              disabled={isSubmitting}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {/* Spacer to balance layout when buttons are not shown */}
                  {!editNote && <div className="w-9"></div>}
                </>
              )}
            </div>
          </div>

          {/* Form */}
          <form 
            ref={formRef} 
            onSubmit={handleSubmit} 
            className="flex-1 flex flex-col overflow-hidden"
            style={(() => {
              // Get cluster color from formData (edit mode) or editNote (view mode)
              let clusterColor = null;
              if (isEditMode) {
                if (formData.cluster_id) {
                  const selectedCluster = clusters.find(c => c.id === formData.cluster_id);
                  if (selectedCluster) {
                    clusterColor = selectedCluster.color;
                  }
                }
                // If cluster_id is null, no color (white background)
              } else if (editNote?.cluster?.color) {
                clusterColor = editNote.cluster.color;
              }
              return clusterColor ? { backgroundColor: getLightBackgroundColor(clusterColor) } : {};
            })()}
          >
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Title */}
              <div className="px-6 pt-2 pb-2">
                {/* Dates at top */}
                {editNote && (
                  <div className="flex items-center justify-between mb-2 text-xs text-gray-500">
                    <span>Updated: {formatDate(editNote.updated_at)}</span>
                    <span>Created: {formatDate(editNote.created_at)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-4 min-w-0">
                  {!isEditMode && !(editNote?.title || '').trim() ? (
                    <span className="flex-1 min-w-0 text-2xl font-semibold text-gray-400 italic">
                      NO TITLE
                    </span>
                  ) : (
                    <input
                      type="text"
                      name="title"
                      value={isEditMode ? formData.title : (editNote?.title || '')}
                      onChange={handleChange}
                      disabled={!isEditMode}
                      className={`flex-1 min-w-0 text-2xl font-semibold text-gray-800 bg-transparent border-none focus:outline-none placeholder:text-gray-400 ${
                        !isEditMode ? 'cursor-default' : ''
                      }`}
                      placeholder="Title"
                    />
                  )}
                  {/* Cluster Color Button - Only in edit mode */}
                  {isEditMode && (
                    <div className="relative flex-shrink-0" ref={clusterDropdownRef}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setShowClusterDropdown(!showClusterDropdown);
                        }}
                        className="w-8 h-8 rounded-full border-2 hover:opacity-80 transition-opacity flex items-center justify-center shadow-sm"
                        style={(() => {
                          // Get cluster color from formData (edit mode)
                          const selectedClusterId = formData.cluster_id;
                          const selectedCluster = selectedClusterId 
                            ? clusters.find(c => c.id === selectedClusterId)
                            : null;
                          const clusterColor = selectedCluster?.color || '#9CA3AF';
                          const borderColor = selectedCluster ? clusterColor : '#D1D5DB';
                          return {
                            backgroundColor: clusterColor,
                            borderColor: borderColor
                          };
                        })()}
                        title={(() => {
                          const selectedClusterId = formData.cluster_id;
                          const selectedCluster = selectedClusterId 
                            ? clusters.find(c => c.id === selectedClusterId)
                            : null;
                          return selectedCluster?.name || 'No cluster - Click to assign';
                        })()}
                      />
                      {/* Cluster Dropdown */}
                      {showClusterDropdown && (
                        <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[200px] z-50 max-h-[360px] overflow-y-auto">
                          {isLoadingClusters ? (
                            <div className="px-4 py-2 text-xs text-gray-500 flex items-center gap-2">
                              <SpinnerLoad size="small" />
                              Loading clusters...
                            </div>
                          ) : (
                            <>
                              {/* No cluster option */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setShowClusterDropdown(false);
                                  setFormData(prev => ({ ...prev, cluster_id: null }));
                                }}
                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                                  !formData.cluster_id ? 'text-green-600 bg-green-50' : 'text-gray-700'
                                }`}
                              >
                                <div className="w-3 h-3 rounded-full border border-gray-300" />
                                <span>No cluster</span>
                              </button>
                              {/* Cluster options */}
                              {clusters.map((cluster) => (
                                <button
                                  key={cluster.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setShowClusterDropdown(false);
                                    setFormData(prev => ({ ...prev, cluster_id: cluster.id }));
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                                    formData.cluster_id === cluster.id ? 'text-green-600 bg-green-50' : 'text-gray-700'
                                  }`}
                                >
                                  <div 
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: cluster.color }}
                                  />
                                  <span>{cluster.name}</span>
                                </button>
                              ))}
                              {clusters.length === 0 && (
                                <div className="px-4 py-2 text-xs text-gray-500 italic">
                                  No clusters available
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col overflow-hidden px-4 pb-4">
                <textarea
                  name="content"
                  // ##### REFACTORED: Use editNote when viewing, formData when editing
                  value={isEditMode ? formData.content : (editNote?.content || '')}
                  onChange={handleChange}
                  disabled={!isEditMode}
                  className={`flex-1 w-full min-h-0 text-base text-gray-700 border-none focus:outline-none resize-none placeholder:text-gray-400 overflow-y-auto custom-scrollbar ${
                    !isEditMode ? 'cursor-default bg-transparent' : ''
                  }`}
                  style={(() => {
                    // Get cluster color for textarea background
                    let clusterColor = null;
                    if (isEditMode && formData.cluster_id) {
                      const selectedCluster = clusters.find(c => c.id === formData.cluster_id);
                      if (selectedCluster) {
                        clusterColor = selectedCluster.color;
                      }
                    } else if (!isEditMode && editNote?.cluster?.color) {
                      clusterColor = editNote.cluster.color;
                    }
                    
                    const baseStyle = isEditMode ? {
                      backgroundImage: 'repeating-linear-gradient(transparent, transparent 1.4em, #e5e7eb 1.4em, #e5e7eb 1.5em)',
                      backgroundAttachment: 'local',
                      lineHeight: '1.5em',
                      backgroundSize: '100% 1.5em',
                      paddingTop: '0.1em'
                    } : {};
                    
                    return clusterColor 
                      ? { ...baseStyle, backgroundColor: getLightBackgroundColor(clusterColor) }
                      : baseStyle;
                  })()}
                  placeholder="Start writing..."
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default DrawerNoteForm;
